import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import { AuthService, UserService } from '../services';

interface IUserLogged {
    id: string;
    name: string;
}

interface IAuthContextData {
    logout: () => void;
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<{ token: string, id: string }>;
    refreshToken: (oldToken: string) => Promise<string | void>;
    userLogged: IUserLogged;
    setAccessToken: (newToken: string) => Promise<string | void>;
    getAccessToken: () => void;
    isSessionValidated: boolean;
}

interface IAuthProviderProps {
    children: React.ReactNode;
}

const AuthContext = createContext({} as IAuthContextData);

const LOCAL_STORAGE_KEY__ACCESS_TOKEN = 'APP_ACCESS_TOKEN';
const LOCAL_STORAGE_KEY__LOGGED_USER_ID = 'LOGGED_USER_ID';
const LOCAL_STORAGE_KEY__LOGGED_USER_NAME = 'LOGGED_USER_NAME';

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
    const [access_token, setAccessToken] = useState<string>();
    const [userLoggedState, setUserLogged] = useState<IUserLogged>({ id: '', name: '' });
    const [isSessionValidated, setIsSessionValidated] = useState(false);

    const handleLogout = useCallback(() => {
        localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEY__LOGGED_USER_ID);
        localStorage.removeItem(LOCAL_STORAGE_KEY__LOGGED_USER_NAME);

        setAccessToken(undefined);
        setUserLogged({ id: '', name: '' });
        setIsSessionValidated(true);
    }, []);

    const handleLogin = useCallback(async (username: string, password: string) => {
        const user = await AuthService.auth(username, password);

        if (user instanceof Error) {
            return { token: '', id: '' };
        }

        localStorage.setItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN, user.access_token);
        localStorage.setItem(LOCAL_STORAGE_KEY__LOGGED_USER_ID, user.id);
        localStorage.setItem(LOCAL_STORAGE_KEY__LOGGED_USER_NAME, user.name);

        setUserLogged({ id: user.id, name: user.name });
        setAccessToken(user.access_token);

        return { token: user.access_token, id: user.id };
    }, []);

    const handleSetAccessToken = useCallback(async (newAccessToken: string) => {
        localStorage.setItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN, newAccessToken);
        setAccessToken(newAccessToken);
    }, []);

    const handleRefreshToken = useCallback(async (oldToken: string) => {
        const result = await AuthService.refreshToken(oldToken);

        if (result instanceof Error) {
            return result.message;
        }

        localStorage.setItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN, result.access_token);
        setAccessToken(result.access_token);
    }, []);

    const handleGetToken = async () => {
        const token = localStorage.getItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
        const id = localStorage.getItem(LOCAL_STORAGE_KEY__LOGGED_USER_ID);
        const name = localStorage.getItem(LOCAL_STORAGE_KEY__LOGGED_USER_NAME);

        if (token && id && name) {
            setAccessToken(token);
            setUserLogged({ id, name });

            return { token, id };
        }

        return {};
    };

    useEffect(() => {
        const restoreAndValidate = async () => {
            const { id } = await handleGetToken();

            if (!id) {
                handleLogout();
                return;
            }

            try {
                const user = await UserService.getById(id);

                if (user instanceof Error) {
                    handleLogout();
                    return;
                }
            } catch (error) {
                handleLogout();
                return;
            } finally {
                setIsSessionValidated(true);
            }
        };

        restoreAndValidate();
    }, [handleLogout]);

    useEffect(() => {
        const handleUnauthorized = () => {
            handleLogout();
        };

        window.addEventListener('auth:unauthorized', handleUnauthorized);

        return () => {
            window.removeEventListener('auth:unauthorized', handleUnauthorized);
        };
    }, [handleLogout]);

    const isAuthenticated = useMemo(() => {
        return !!(access_token && userLoggedState.id);
    }, [access_token, userLoggedState.id]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                isSessionValidated,
                login: handleLogin,
                logout: handleLogout,
                refreshToken: handleRefreshToken,
                userLogged: userLoggedState,
                setAccessToken: handleSetAccessToken,
                getAccessToken: handleGetToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => useContext(AuthContext);